(() => {
  const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;
  const desktop = matchMedia('(min-width: 960px)');
  const clamp = (value, min = 0, max = 1) => Math.min(max, Math.max(min, value));
  const ease = (value) => 1 - Math.pow(1 - clamp(value), 3);
  const smooth = (value) => { const t = clamp(value); return t * t * (3 - 2 * t); };
  const mix = (from, to, progress) => from + (to - from) * progress;
  let frame = 0;

  const layoutStage = (stage) => {
    const sticky = stage.querySelector('.wr-mira-image-stage__sticky');
    const tiles = [...stage.querySelectorAll('[data-mira-stage-tile]')].slice(0, 9);
    if (!sticky || !tiles.length) return;

    const rect = stage.getBoundingClientRect();
    const range = Math.max(1, stage.offsetHeight - innerHeight);
    const raw = reduced ? 1 : clamp(-rect.top / range);
    // Keep the collage moving at a constant rate through almost the complete
    // sticky range. Easing here made the final placement appear to stall.
    const shrink = clamp((raw - .02) / .94);
    const bounds = sticky.getBoundingClientRect();
    const mobile = !desktop.matches;
    const gap = mobile ? 4 : 6;
    const inset = 3;
    let targets = [];
    let startMain;

    if (mobile) {
      const cellWidth = Math.min(bounds.width * .72, 286);
      const cellHeight = cellWidth * 4 / 3;
      const centerLeft = (bounds.width - cellWidth) / 2;
      const left = centerLeft - cellWidth - gap;
      const right = centerLeft + cellWidth + gap;
      const centerTop = (bounds.height - cellHeight) / 2;
      const centerUpper = centerTop - cellHeight - gap;
      const centerLower = centerTop + cellHeight + gap;
      const leftUpper = -cellHeight * .78;
      const rightUpper = -cellHeight * .42;
      targets = [
        { left:centerLeft, top:centerTop, width:cellWidth, height:cellHeight },
        { left:centerLeft, top:centerUpper, width:cellWidth, height:cellHeight },
        { left:centerLeft, top:centerLower, width:cellWidth, height:cellHeight },
        { left, top:leftUpper, width:cellWidth, height:cellHeight },
        { left, top:leftUpper + cellHeight + gap, width:cellWidth, height:cellHeight },
        { left, top:leftUpper + (cellHeight + gap) * 2, width:cellWidth, height:cellHeight },
        { left:right, top:rightUpper, width:cellWidth, height:cellHeight },
        { left:right, top:rightUpper + cellHeight + gap, width:cellWidth, height:cellHeight },
        { left:right, top:rightUpper + (cellHeight + gap) * 2, width:cellWidth, height:cellHeight },
      ];
      const startWidth = bounds.width - inset * 2;
      const startHeight = Math.min(bounds.height - inset * 2, startWidth * 4 / 3);
      startMain = { left:inset, top:(bounds.height - startHeight) / 2, width:startWidth, height:startHeight };
    } else {
      const centerWidth = Math.min(bounds.width * .68, bounds.height * 1.62);
      const centerHeight = centerWidth * 9 / 16;
      const sideWidth = Math.min(bounds.width * .31, bounds.height * .54);
      const sideHeight = sideWidth * 1.12;
      const centerLeft = (bounds.width - centerWidth) / 2;
      const left = centerLeft - sideWidth - gap;
      const right = centerLeft + centerWidth + gap;
      const centerTop = (bounds.height - centerHeight) / 2;
      const centerUpper = centerTop - centerHeight - gap;
      const centerLower = centerTop + centerHeight + gap;
      const leftUpper = -sideHeight * .88;
      const rightUpper = -sideHeight * .66;
      targets = [
        { left:centerLeft, top:centerTop, width:centerWidth, height:centerHeight },
        { left:centerLeft, top:centerUpper, width:centerWidth, height:centerHeight },
        { left:centerLeft, top:centerLower, width:centerWidth, height:centerHeight },
        { left, top:leftUpper, width:sideWidth, height:sideHeight },
        { left, top:leftUpper + sideHeight + gap, width:sideWidth, height:sideHeight },
        { left, top:leftUpper + (sideHeight + gap) * 2, width:sideWidth, height:sideHeight },
        { left:right, top:rightUpper, width:sideWidth, height:sideHeight },
        { left:right, top:rightUpper + sideHeight + gap, width:sideWidth, height:sideHeight },
        { left:right, top:rightUpper + (sideHeight + gap) * 2, width:sideWidth, height:sideHeight },
      ];
      const startWidth = bounds.width - inset * 2;
      const startHeight = Math.min(bounds.height - inset * 2, startWidth * 9 / 16);
      startMain = { left:inset, top:(bounds.height - startHeight) / 2, width:startWidth, height:startHeight };
    }

    tiles.forEach((tile, index) => {
      const target = targets[index] || targets[0];
      let start = startMain;
      if (index > 0) {
        const column = index < 3 ? 'center' : index < 6 ? 'left' : 'right';
        const verticalDirection = index % 3 === 1 ? -1 : index % 3 === 2 ? 1 : 0;
        start = {
          left: target.left + (column === 'left' ? -bounds.width * .46 : column === 'right' ? bounds.width * .46 : 0),
          top: target.top + verticalDirection * bounds.height * .82,
          width: target.width,
          height: target.height,
        };
      }
      const delay = index === 0 ? 0 : .018 + (index % 3) * .012;
      const local = index === 0 ? shrink : clamp((shrink - delay) / Math.max(.25, 1 - delay));
      tile.style.left = `${mix(start.left, target.left, local)}px`;
      tile.style.top = `${mix(start.top, target.top, local)}px`;
      tile.style.width = `${mix(start.width, target.width, local)}px`;
      tile.style.height = `${mix(start.height, target.height, local)}px`;
      tile.style.opacity = index === 0 ? '1' : String(clamp(local * 1.45));
      tile.style.zIndex = index === 0 ? String(20 - Math.round(shrink * 11)) : String(6 + index);
    });

    const intro = ease(clamp((raw - .08) / .18));
    const copyOpacity = clamp(intro * 1.1);
    // Darkening begins with the first visible shrink and remains tied to the
    // same linear progress, so it is clearly perceptible throughout.
    const stageDim = shrink * .34;
    stage.style.setProperty('--stage-dim', stageDim.toFixed(3));
    stage.style.setProperty('--stage-copy-opacity', copyOpacity.toFixed(3));
    stage.style.setProperty('--stage-copy-scale', (.94 + intro * .06 + shrink * .035).toFixed(3));
    stage.style.setProperty('--stage-copy-color', '#fff');
    stage.style.setProperty('--stage-logo-filter', 'brightness(0) invert(1)');
    stage.dataset.stageComplete = raw > .97 ? 'true' : 'false';
  };

  const layoutScrollStrip = (strip) => {
    const track = strip.querySelector('.wr-mira-strip__track');
    if (!track) return;
    const rect = strip.getBoundingClientRect();
    const progress = reduced ? 0 : clamp((innerHeight - rect.top) / Math.max(1, innerHeight + rect.height));
    const travel = Math.max(0, track.scrollWidth - strip.clientWidth);
    track.style.transform = `translate3d(${-travel * progress}px,0,0)`;
  };

  const update = () => {
    document.querySelectorAll('[data-mira-image-stage]').forEach(layoutStage);
    document.querySelectorAll('[data-scroll-strip]').forEach(layoutScrollStrip);
    frame = 0;
  };
  const requestUpdate = () => { if (!frame) frame = requestAnimationFrame(update); };

  document.addEventListener('click', (event) => {
    const answerButton = event.target.closest('[data-answer-toggle]');
    if (answerButton) {
      const item = answerButton.closest('[data-answer]');
      const panel = item?.querySelector('[data-answer-panel]');
      const open = !item?.classList.contains('is-open');
      item?.classList.toggle('is-open', open);
      answerButton.setAttribute('aria-expanded', String(open));
      if (panel) panel.style.maxHeight = open ? `${panel.scrollHeight}px` : '0px';
    }

    const reel = event.target.closest('[data-media-reel]');
    if (reel) {
      const hero = reel.closest('.wr-mira-media-hero');
      // The current 3D reel owns this control. Keep the legacy video toggle
      // disabled so one click cannot open and immediately close the new reel.
      if (hero?.hasAttribute('data-wr-reel-root')) return;
      const video = hero?.querySelector('video');
      hero?.classList.toggle('is-reel-active');
      if (video) video.paused ? video.play().catch(() => {}) : video.pause();
    }
  });

  const setupDrawerHover = (name) => {
    const trigger = document.querySelector('[data-drawer-open="' + name + '"]');
    const drawer = document.querySelector('[data-drawer="' + name + '"]');
    if (!trigger || !drawer) return;
    let openedByHover = false;
    let closeTimer = 0;
    const cancelClose = () => { if (closeTimer) clearTimeout(closeTimer); closeTimer = 0; };
    const scheduleClose = () => {
      if (!openedByHover || !desktop.matches) return;
      cancelClose();
      closeTimer = window.setTimeout(() => {
        closeTimer = 0;
        if (trigger.matches(':hover') || drawer.matches(':hover')) return;
        if (document.body.dataset.openDrawer === name || drawer.classList.contains('is-open')) {
          delete trigger.dataset.wrHoverOpenedAt;
          trigger.click();
          trigger.blur();
        }
        openedByHover = false;
      // Allow the header-colour delay to finish before deciding that the
      // pointer missed the panel; otherwise a quick move off the icon can
      // close the drawer just before it becomes visible.
      }, 560);
    };
    trigger.addEventListener('pointerenter', (event) => {
      if (!desktop.matches || (event.pointerType && event.pointerType !== 'mouse')) return;
      cancelClose();
      if (document.body.dataset.openDrawer !== name && trigger.getAttribute('aria-expanded') !== 'true') {
        openedByHover = true;
        trigger.click();
        trigger.dataset.wrHoverOpenedAt = String(Date.now());
        trigger.blur();
      }
    }, { passive:true });
    trigger.addEventListener('pointerleave', scheduleClose, { passive:true });
    drawer.addEventListener('pointerenter', cancelClose, { passive:true });
    drawer.addEventListener('pointerleave', scheduleClose, { passive:true });
    new MutationObserver(() => {
      if (document.body.dataset.openDrawer !== name) {
        openedByHover = false;
        delete trigger.dataset.wrHoverOpenedAt;
        cancelClose();
      }
    }).observe(document.body, { attributes:true, attributeFilter:['data-open-drawer'] });
  };
  setupDrawerHover('menu');
  setupDrawerHover('cart');

  window.addEventListener('scroll', requestUpdate, { passive:true });
  window.addEventListener('resize', requestUpdate, { passive:true });
  document.addEventListener('shopify:section:load', requestUpdate);
  window.setTimeout(() => document.documentElement.classList.remove('wr-page-enter'), reduced ? 0 : 980);
  document.documentElement.classList.add('wr-mira-v10-ready');
  update();
})();
